export interface LocalImageModel {
  id: string;
  name: string;
  source: string;
  type: 'checkpoint' | 'unet' | string;
  provider?: string;
  status?: string;
}

export interface LocalVideoModel {
  name: string;
  type: string;
  supportedModes: string[];
  provider?: string;
  status?: string;
}

export type LocalVideoMode = 't2v' | 'i2v';

export interface LocalVoice {
  id: string;
  name: string;
  gender?: string;
}

// null means the catalog has not supplied its initial choice yet; an empty
// string means a previously selected item disappeared and must be reselected.
export function reconcileSelection<T>(current: string | null, available: T[], id: (item: T) => string): string {
  if (current === null) return available.length > 0 ? id(available[0]) : '';
  return available.some((item) => id(item) === current) ? current : '';
}

export const SUPPORTED_PROVIDERS = ['comfyui', 'google_flow_browser', 'gemini_web'] as const;

export function catalogProvider(provider: string): string {
  return provider === 'local_sd' ? 'comfyui' : provider === 'google_flow' ? 'google_flow_browser' : provider;
}

function matchesProvider(modelProvider: string | undefined, selectedProvider?: string): boolean {
  return !selectedProvider || (modelProvider ?? 'comfyui') === catalogProvider(selectedProvider);
}

export function isSupportedProvider(provider?: string): boolean {
  if (!provider) return true;
  return (SUPPORTED_PROVIDERS as readonly string[]).includes(provider);
}

export function usableImageModels(models: LocalImageModel[], provider?: string): LocalImageModel[] {
  return models.filter((model) => (model.type === 'checkpoint' || model.type === 'online' || model.type === 'browser')
    && Boolean(model.id) && isSupportedProvider(model.provider) && matchesProvider(model.provider, provider)
    && (!model.status || model.status === 'ready'));
}

export function usableVideoModels(models: LocalVideoModel[], provider?: string): LocalVideoModel[] {
  return models.filter((model) => isSupportedProvider(model.provider) && matchesProvider(model.provider, provider) && Boolean(model.name)
    && (!model.status || model.status === 'ready')
    && model.supportedModes.some((mode) => mode === 't2v' || mode === 'i2v'));
}

export function videoModelsForMode(models: LocalVideoModel[], mode: LocalVideoMode): LocalVideoModel[] {
  return models.filter((model) => model.supportedModes.includes(mode));
}
